import { useState, useEffect, useCallback } from "react";
import {
  getDocByQuery,
  upsertDoc,
  getAllContentsByReference,
} from "repositories/firebase";
import { COLLECTIONS } from "constant";
import { Content } from "types";
import { db } from "@firebase";

export function useContents() {
  const [contents, setContents] = useState<Content[]>([]);
  const [contentLoading, setLoading] = useState<boolean>(true);
  const [contentError, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState("ViewAll");

  const fetchContents = async () => {
    try {
      setLoading(true);
      const contentList = await getAllContentsByReference(db);

      if (contentList.length > 0) {
        setContents(contentList);
      } else {
        setContents([]);
      }

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch data");
      console.error("Error fetching contents:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
  }, []);

  const fetchContentById = useCallback(async (contentId: string) => {
    try {
      const fetchedContent = await getDocByQuery<Content>(
        COLLECTIONS.CONTENTS,
        "id",
        contentId
      );
      if (fetchedContent) {
        return {
          data: fetchedContent,
          error: null,
        };
      } else {
        return {
          data: null,
          error: "Content not found",
        };
      }
    } catch (err) {
      return {
        data: null,
        error: err instanceof Error ? err.message : "Failed to fetch content",
      };
    }
  }, []);

  const updateContentById = useCallback(
    async (contentId: string, contentData: Partial<Content>) => {
      try {
        const completeContentData: Content = {
          ...contentData,
          id: contentId,
        } as Content;
        const response = await upsertDoc<Content>(
          COLLECTIONS.CONTENTS,
          completeContentData,
          contentId
        );
        if (response.success) {
          return {
            success: true,
            message: "Content updated successfully!",
            data: response.data,
          };
        } else {
          return {
            success: false,
            message: response.message || "Failed to update content",
          };
        }
      } catch (err) {
        return {
          success: false,
          message:
            err instanceof Error ? err.message : "Failed to update content",
        };
      }
    },
    []
  );

  return {
    contents,
    contentLoading,
    contentError,
    fetchContents,
    selectedType,
    setSelectedType,
    fetchContentById,
    updateContentById,
    setContents,
  };
}
